import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  GrowthService,
  type GrowthCategory,
  GrowthCategoryWithMedia,
} from '@mc/api'
import { useStaticData } from '@mc/static-data-react'
import type { getCategories, getV2Categories } from 'libs/server/api'

const staleTime = 1000 * 60 * 60 // 1 hour

export const useGrowthCategories = () => {
  const serverCategories = useSSRPlaceholderGrowthCategories()
  return useQuery({
    queryKey: ['growth-categories'],
    queryFn: () => GrowthService.getGrowthCategories(),
    // the SSR categories are parent cats only, so they're only good for placeholder data.
    // but, if they are available, having them populated immediately will let us render
    // the first menu (parent cats) of the navbar Browse component instantly.
    placeholderData: serverCategories,
    staleTime,
  })
}

const useSSRPlaceholderGrowthCategories = () => {
  const { categories: serverCategories } = useStaticData<{
    categories?: Awaited<
      ReturnType<typeof getV2Categories | typeof getCategories>
    >
  }>()

  if (!serverCategories) return undefined
  const serverCategory = serverCategories?.[0]
  // we only want to return the placeholder categories if they are GrowthCategory objects
  // otherwise, don't use them
  if (serverCategory && 'uuid' in serverCategory) {
    return serverCategories as GrowthCategory[]
  }
  return undefined
}

export const useGrowthQuizCategoryMedia = () =>
  useQuery(
    ['growth-quiz-category-media'],
    () => GrowthService.getQuizCategoryMedia(),
    {
      staleTime,
    },
  )

export const useGrowthQuizCategoryMediaById = (
  categoryId?: string | number,
) => {
  const { data } = useGrowthQuizCategoryMedia()

  const getCategoryById = useCallback(
    (id?: string | number) =>
      id ? data?.find((c) => String(c.id) === String(id)) : undefined,
    [data],
  )

  const getCategoryPrimaryMedia = useCallback(
    (category?: GrowthCategoryWithMedia) =>
      category?.media?.find((v) => v.mediaType === 'video_16x9'),
    [],
  )

  const category = useMemo(
    () => getCategoryById(categoryId),
    [getCategoryById, categoryId],
  )

  const parentCategoryWithMedia = useMemo(
    () => getCategoryById(category?.parentId),
    [category?.parentId, getCategoryById],
  )

  return {
    category,
    parentCategoryWithMedia,
    getCategoryPrimaryMedia,
  }
}
